<div class="editor-container fade-in" [style.display]="isFramesEditorOpen ? 'none' : 'flex'">
  <!-- Header Section -->
  <header class="editor-header">
    <div class="header-left">
      <button class="back-btn" (click)="goBack()">
        <svg class="chevron-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Configuration
      </button>
      <div class="title-meta" style="display: flex; align-items: center; gap: 16px;">
        <div>
          <h2 style="margin: 0;">Theme Layout Editor</h2>
          <div style="margin-top: 4px; display: flex; flex-direction: column; gap: 4px;">
            <div>
              <span class="meta-badge" [class.global-badge]="isGlobal" [class.standalone-badge]="!isGlobal && overrideGlobalLayout" [class.device-badge]="!isGlobal && !overrideGlobalLayout">
                {{ isGlobal ? 'Global Layout' : (overrideGlobalLayout ? '✨ Standalone Device Layout' : 'Device Layout') }}
              </span>
            </div>
            <div class="meta-details" *ngIf="!isGlobal" style="font-size: 0.75rem; color: #64748b; letter-spacing: 0.5px; display: flex; flex-direction: column; gap: 2px;">
              <div>STORE: <strong style="color: #475569; font-weight: 600;">{{ storeId }}</strong></div>
              <div>DEVICE: <strong style="color: #475569; font-weight: 600;">{{ deviceId }}</strong></div>
            </div>
          </div>
        </div>
        <select class="version-select" [(ngModel)]="selectedVersion" (change)="onVersionChange()" title="View Version">
          <option value="">Draft</option>
          <option *ngFor="let v of publishedVersions" [value]="v.id">
            {{ v.id === activePublishedVersion ? '✓ ' : '' }}Version {{ v.version }} ({{ v.publishedAt |
            date:'short' }})
          </option>
        </select>

        <!-- Compact Background Uploader Widget -->
        <div class="header-bg-widget" style="display: flex; align-items: center; gap: 8px; background: #1e293b; padding: 6px 12px; border-radius: 6px; border: 1px solid #334155;">
          <span style="color: #94a3b8; font-size: 0.8rem; font-weight: 600;">BG:</span>
          
          <div *ngIf="backgroundPreview" style="display: flex; align-items: center; gap: 6px;">
             <img [src]="backgroundPreview.url" style="height: 20px; border-radius: 2px; object-fit: contain;">
             <button *ngIf="!version" class="icon-action-btn danger btn-xs" style="margin: 0; padding: 0 4px; font-size: 0.75rem;" (click)="removeBackground()" [disabled]="isUploadingBackground" title="Remove Background">🗑️</button>
          </div>
          <div *ngIf="!backgroundPreview && !isLoadingBackground" style="color: #64748b; font-size: 0.8rem; font-style: italic;">
             None
          </div>
          
          <div *ngIf="!version && isFullEditorUnlocked">
             <label for="layoutBackgroundUpload" class="secondary-btn" [class.disabled]="isUploadingBackground"
               style="cursor: pointer; margin: 0; padding: 2px 8px; font-size: 0.75rem; white-space: nowrap;">
               <span *ngIf="!isUploadingBackground">Upload</span>
               <span *ngIf="isUploadingBackground">...</span>
             </label>
             <input id="layoutBackgroundUpload" type="file" accept=".png" (change)="uploadBackground($event)"
               [disabled]="isUploadingBackground" style="display: none;">
          </div>
          <span *ngIf="isLoadingBackground" class="spinner-inline" style="width: 12px; height: 12px; border-color: #3b82f6; border-bottom-color: transparent;"></span>
        </div>
      </div>
    </div>

    <div class="header-right">
      <button class="secondary-btn flex-center mr-2" *ngIf="!isGlobal && !version" (click)="copyFromGlobal()"
        [disabled]="isLoading">
        <span class="btn-icon">📋</span> {{ overrideGlobalLayout ? 'Re-Clone from Global' : 'Copy Global Layout' }}
      </button>
      <button class="secondary-btn flex-center mr-2" *ngIf="!isGlobal && !version" (click)="publishToDevice()"
        [disabled]="isLoading || isPublishingDevice">
        <span class="spinner-inline" *ngIf="isPublishingDevice"></span>
        <span class="btn-icon" *ngIf="!isPublishingDevice">🚀</span> {{ isPublishingDevice ? 'Publishing...' : 'Publish to Device' }}
      </button>
      <button class="primary-btn flex-center" *ngIf="!version" (click)="saveLayout()" [disabled]="isLoading">
        <span class="spinner-inline" *ngIf="isLoading"></span>
        <span class="btn-icon" *ngIf="!isLoading">💾</span> Save Layout to Cloud
      </button>
    </div>
  </header>

  <!-- Read-Only Banner -->
  <div class="read-only-banner" *ngIf="version">
    <div class="banner-content">
      <span class="banner-icon">⚠️</span>
      <p><strong>Viewing Historical Version:</strong> This layout is in read-only mode. To make changes, switch to the
        <strong>Draft</strong> version in the dropdown above.
      </p>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-overlay-container" *ngIf="isLoading">
    <div class="spinner"></div>
    <p class="mt-3 text-muted">Synchronizing layout configuration...</p>
  </div>

  <!-- Main Workspace -->
  <div class="editor-workspace" *ngIf="!isLoading">
    <!-- Viewport Container (16:9 FHD Simulation) -->
    <div class="viewport-outer">

      <div class="viewport-label">
        <span>Photo Booth FHD Kiosk Screen Simulator (1920x1080 - 16:9 Aspect Ratio)</span>
      </div>

      <div class="viewport-inner-16-9">
        <div class="kiosk-free-canvas" (click)="onCanvasClick($event)">
          <!-- Layout Background Image -->
          <div class="canvas-bg-overlay" *ngIf="backgroundPreview" [style.background-image]="'url(' + backgroundPreview.url + ')'"></div>

          <!-- Free-Form Drag & Resize Tiles -->
          <div *ngFor="let item of dashboard"
               class="kiosk-free-tile"
               [class.item-active]="activeItem?.id === item.id"
               [class.locked-tile]="!isFullEditorUnlocked && !item.properties.isCustomizable"
               [style.left.%]="(item.x / 1920) * 100"
               [style.top.%]="(item.y / 1080) * 100"
               [style.width.%]="(item.cols / 1920) * 100"
               [style.height.%]="(item.rows / 1080) * 100"
               (mousedown)="startTileDrag($event, item)"
               (click)="selectTile(item); $event.stopPropagation()">

            <!-- Selection Indicator Badge -->
            <div class="selected-badge" *ngIf="activeItem?.id === item.id">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Selected
            </div>

            <!-- Tile Content Visual Anatomy -->
            <div class="tile-card" [style.background-image]="tileThumbnails[item.id] ? 'url(\'' + tileThumbnails[item.id] + '\')' : null">
              <img *ngIf="tileThumbnails[item.id]" [src]="tileThumbnails[item.id]" class="tile-bg-image" alt="Theme Thumbnail">
              <div class="tile-overlay" [class.has-image]="!!tileThumbnails[item.id]"></div>

              <div class="tile-inner-content">
                <!-- Image Placeholder if no thumbnail uploaded -->
                <div class="tile-image-fallback" *ngIf="!tileThumbnails[item.id]">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" class="fallback-icon">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                </div>

                <div class="tile-text-info drag-ignore" cdkDrag (cdkDragEnded)="onTextDragEnded($event, item)"
                  [cdkDragFreeDragPosition]="getDragPosition(item)" [cdkDragDisabled]="!!version || !isFullEditorUnlocked">
                  <h3 class="tile-title" [style.color]="item.properties.titleColor || '#ffffff'"
                    [style.font-size]="'calc(' + (item.properties.titleFontSize || 18) + ' * (100cqw / 1920))'"
                    [style.font-weight]="item.properties.titleBold ? 'bold' : 'normal'"
                    [style.font-style]="item.properties.titleItalic ? 'italic' : 'normal'"
                    [style.text-decoration]="item.properties.titleUnderline ? 'underline' : 'none'">
                    {{ item.properties.title }}
                  </h3>
                  <p class="tile-subtitle" [style.color]="item.properties.subtitleColor || '#e2e8f0'"
                    [style.font-size]="'calc(' + (item.properties.subtitleFontSize || 14) + ' * (100cqw / 1920))'"
                    [style.font-weight]="item.properties.subtitleBold ? 'bold' : 'normal'"
                    [style.font-style]="item.properties.subtitleItalic ? 'italic' : 'normal'"
                    [style.text-decoration]="item.properties.subtitleUnderline ? 'underline' : 'none'">
                    {{ item.properties.subTitle }}
                  </p>
                </div>
              </div>

              <!-- Item Corner Hover Actions -->
              <div class="gridster-item-content-actions drag-ignore" *ngIf="!version">
                <button class="tile-action-btn edit-action" title="Edit Properties"
                  (click)="selectTile(item); $event.stopPropagation()">
                  ✏️
                </button>
                <button class="tile-action-btn delete-action" title="Delete Tile"
                  (click)="removeTile(item); $event.stopPropagation()">
                  🗑️
                </button>
              </div>
            </div>

            <!-- Free Resize Handles (shown when active & editable) -->
            <ng-container *ngIf="activeItem?.id === item.id && !version">
              <div class="resize-handle handle-nw" (mousedown)="startTileResize($event, item, 'nw')"></div>
              <div class="resize-handle handle-n" (mousedown)="startTileResize($event, item, 'n')"></div>
              <div class="resize-handle handle-ne" (mousedown)="startTileResize($event, item, 'ne')"></div>
              <div class="resize-handle handle-e" (mousedown)="startTileResize($event, item, 'e')"></div>
              <div class="resize-handle handle-se" (mousedown)="startTileResize($event, item, 'se')"></div>
              <div class="resize-handle handle-s" (mousedown)="startTileResize($event, item, 's')"></div>
              <div class="resize-handle handle-sw" (mousedown)="startTileResize($event, item, 'sw')"></div>
              <div class="resize-handle handle-w" (mousedown)="startTileResize($event, item, 'w')"></div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar Editor Panel -->
    <aside class="sidebar-editor">
      <div class="sidebar-section" *ngIf="!version">
        <button class="add-tile-btn btn-block" (click)="addTile()">
          <span class="btn-icon-large">➕</span> Add New Selection Tile
        </button>
      </div>

      <div class="sidebar-divider"></div>

      <!-- Properties Form -->
      <div class="sidebar-section flex-grow">
        <div class="section-title-container">
          <div class="section-title-header">
            <div>
              <h4 class="section-title-heading">Tile Properties</h4>
              <span class="tile-id" *ngIf="activeItem">ID: {{ activeItem.id }}</span>
            </div>
            <button type="button" class="delete-tile-btn" *ngIf="activeItem && !version" (click)="removeTile(activeItem)" title="Delete this tile">
              🗑️ Delete Tile
            </button>
          </div>
        </div>

        <div *ngIf="!activeItem" class="empty-selection-msg">
          <p>No tile selected.</p>
          <p class="hint">Click an item on the screen simulator or create a new one to modify its settings.</p>
        </div>

        <form *ngIf="activeItem" class="properties-form">
          <div class="form-group" *ngIf="isGlobal">
            <label class="custom-toggle-container">
              <div class="custom-toggle">
                <input type="checkbox" name="isCustomizable" [(ngModel)]="activeItem.properties.isCustomizable"
                  (ngModelChange)="syncDirtyState()" [disabled]="!!version">
                <span class="toggle-slider"></span>
              </div>
              <span class="toggle-label-text">Allow Device Customization</span>
            </label>
            <p class="input-helper" style="margin-top: 6px;">When enabled, this tile's content can be customized in
              specific device settings.</p>
          </div>

          <div class="form-group" *ngIf="!isFullEditorUnlocked && activeItem.properties.isCustomizable" style="background: rgba(59,130,246,0.1); padding: 12px; border-radius: 6px; border: 1px solid rgba(59,130,246,0.3); margin-bottom: 16px;">
            <label class="custom-toggle-container" style="margin: 0; display: flex; align-items: center; cursor: pointer;">
              <div class="custom-toggle">
                <input type="checkbox" name="isCustomized" [(ngModel)]="activeItem.properties.isCustomized"
                  (ngModelChange)="syncDirtyState()" [disabled]="!!version">
                <span class="toggle-slider"></span>
              </div>
              <span class="toggle-label-text" style="color: #ffffff; font-weight: 500; margin-left: 8px;">Customize this tile</span>
            </label>
            <p class="input-helper" style="margin-top: 8px; color: #000000; font-size: 0.85rem;" *ngIf="!activeItem.properties.isCustomized">
              Currently inheriting from the Global layout. Turn on to override properties for this machine.
            </p>
          </div>

          <fieldset [disabled]="!isFullEditorUnlocked && !activeItem.properties.isCustomized" style="border: none; padding: 0; margin: 0; min-width: 0;">

          <!-- Position & Dimension Section -->
          <div class="form-group" *ngIf="isFullEditorUnlocked" style="background: rgba(15, 23, 42, 0.5); padding: 10px 12px; border-radius: 8px; border: 1px solid #334155; margin-bottom: 16px;">
            <label style="display: block; font-weight: 600; font-size: 0.85rem; color: #cbd5e1; margin-bottom: 8px;">
              📍 Position &amp; Dimensions (Pixels)
            </label>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
              <div>
                <span style="font-size: 0.72rem; color: #94a3b8;">X:</span>
                <input type="number" name="tileX" class="form-input" [(ngModel)]="activeItem.x" (ngModelChange)="clampActiveItem()" min="0" max="1920" [disabled]="!!version" style="padding: 4px 8px; font-size: 0.85rem;">
              </div>
              <div>
                <span style="font-size: 0.72rem; color: #94a3b8;">Y:</span>
                <input type="number" name="tileY" class="form-input" [(ngModel)]="activeItem.y" (ngModelChange)="clampActiveItem()" min="0" max="1080" [disabled]="!!version" style="padding: 4px 8px; font-size: 0.85rem;">
              </div>
              <div>
                <span style="font-size: 0.72rem; color: #94a3b8;">Width:</span>
                <input type="number" name="tileCols" class="form-input" [(ngModel)]="activeItem.cols" (ngModelChange)="clampActiveItem()" min="40" max="1920" [disabled]="!!version" style="padding: 4px 8px; font-size: 0.85rem;">
              </div>
              <div>
                <span style="font-size: 0.72rem; color: #94a3b8;">Height:</span>
                <input type="number" name="tileRows" class="form-input" [(ngModel)]="activeItem.rows" (ngModelChange)="clampActiveItem()" min="40" max="1080" [disabled]="!!version" style="padding: 4px 8px; font-size: 0.85rem;">
              </div>
            </div>

            <!-- Quick Alignment Buttons -->
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; margin-top: 6px;">
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('left')" [disabled]="!!version" title="Align Left">⬅️ Left</button>
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('center-h')" [disabled]="!!version" title="Center Horizontally">↔️ Center X</button>
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('right')" [disabled]="!!version" title="Align Right">➡️ Right</button>
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('top')" [disabled]="!!version" title="Align Top">⬆️ Top</button>
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('center-v')" [disabled]="!!version" title="Center Vertically">↕️ Center Y</button>
              <button type="button" class="btn-secondary" style="padding: 3px 4px; font-size: 0.7rem; text-align: center;" (click)="alignTile('bottom')" [disabled]="!!version" title="Align Bottom">⬇️ Bottom</button>
            </div>
          </div>

          <div class="form-group">
            <label for="tileId">Tile ID / Key</label>
            <input id="tileId" type="text" name="tileId" [(ngModel)]="activeItem.id"
              (ngModelChange)="onTileIdChange($event)" placeholder="e.g. classic_strips"
              [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable) || hasTileArtwork(activeItem)"
              [class.is-invalid]="duplicateTileIdError">
            <p class="tile-id-locked-msg" *ngIf="hasTileArtwork(activeItem) || tileIdArtworkBlockedMessage">
              {{ housekeepingMessages.tileIdChangeBlocked }}
            </p>
            <div *ngIf="duplicateTileIdError" class="duplicate-tile-id-error">{{
              duplicateTileIdError }}</div>
          </div>

          <div class="form-group">
            <label for="title" style="display: block; margin-bottom: 6px;">
              <span style="display: block; font-weight: 600; margin-bottom: 6px;">Selection Title</span>
              <div style="display: flex; gap: 8px; align-items: center; justify-content: flex-start;">
                <div
                  style="display: flex; gap: 2px; background: #0f172a; padding: 2px; border-radius: 4px; border: 1px solid #334155;">
                  <button type="button" class="icon-action-btn btn-xs" (click)="toggleTextStyle('title', 'bold')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Bold"
                    style="width: 24px; height: 24px; font-weight: bold; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.titleBold ? '#3b82f6' : 'transparent'">B</button>
                  <button type="button" class="icon-action-btn btn-xs" (click)="toggleTextStyle('title', 'italic')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Italic"
                    style="width: 24px; height: 24px; font-style: italic; font-family: serif; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.titleItalic ? '#3b82f6' : 'transparent'">I</button>
                  <button type="button" class="icon-action-btn btn-xs" (click)="toggleTextStyle('title', 'underline')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Underline"
                    style="width: 24px; height: 24px; text-decoration: underline; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.titleUnderline ? '#3b82f6' : 'transparent'">U</button>
                </div>

                <div
                  style="display: flex; gap: 4px; align-items: center; background: #0f172a; padding: 2px 4px; border-radius: 4px; border: 1px solid #334155;">
                  <button type="button" class="icon-action-btn btn-xs" (click)="adjustFontSize('title', -1)"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 20px; height: 20px; background: transparent; border: none; color: #e2e8f0; cursor: pointer;">-</button>
                  <span style="color: #94a3b8; font-size: 0.75rem; width: 20px; text-align: center;">{{
                    activeItem.properties.titleFontSize || 18 }}</span>
                  <button type="button" class="icon-action-btn btn-xs" (click)="adjustFontSize('title', 1)"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 20px; height: 20px; background: transparent; border: none; color: #e2e8f0; cursor: pointer;">+</button>
                </div>

                <div
                  style="display: flex; align-items: center; background: #0f172a; border-radius: 4px; border: 1px solid #334155; overflow: hidden; height: 26px;">
                  <input type="color" name="titleColorPicker" [(ngModel)]="activeItem.properties.titleColor"
                    (ngModelChange)="syncDirtyState()"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 24px; height: 26px; padding: 0; border: none; cursor: pointer; background: transparent;"
                    title="Title Color">
                  <input type="text" name="titleColorHex" [(ngModel)]="activeItem.properties.titleColor"
                    (ngModelChange)="syncDirtyState()"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 65px; height: 26px; border: none; background: transparent; color: #e2e8f0; font-size: 0.75rem; padding: 0 4px; text-transform: uppercase;"
                    placeholder="#FFF">
                </div>
              </div>
            </label>
            <input id="title" type="text" name="title" [(ngModel)]="activeItem.properties.title"
              (ngModelChange)="syncDirtyState()" placeholder="e.g. Dual Strips Bundle"
              [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" style="width: 100%;">
          </div>

          <div class="form-group">
            <label for="subTitle" style="display: block; margin-bottom: 6px;">
              <span style="display: block; font-weight: 600; margin-bottom: 6px;">Subtitle / Description</span>
              <div style="display: flex; gap: 8px; align-items: center; justify-content: flex-start;">
                <div
                  style="display: flex; gap: 2px; background: #0f172a; padding: 2px; border-radius: 4px; border: 1px solid #334155;">
                  <button type="button" class="icon-action-btn btn-xs" (click)="toggleTextStyle('subtitle', 'bold')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Bold"
                    style="width: 24px; height: 24px; font-weight: bold; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.subtitleBold ? '#3b82f6' : 'transparent'">B</button>
                  <button type="button" class="icon-action-btn btn-xs" (click)="toggleTextStyle('subtitle', 'italic')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Italic"
                    style="width: 24px; height: 24px; font-style: italic; font-family: serif; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.subtitleItalic ? '#3b82f6' : 'transparent'">I</button>
                  <button type="button" class="icon-action-btn btn-xs"
                    (click)="toggleTextStyle('subtitle', 'underline')"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" title="Underline"
                    style="width: 24px; height: 24px; text-decoration: underline; background: transparent; border: none; color: #e2e8f0; cursor: pointer; border-radius: 2px;"
                    [style.background]="activeItem.properties.subtitleUnderline ? '#3b82f6' : 'transparent'">U</button>
                </div>

                <div
                  style="display: flex; gap: 4px; align-items: center; background: #0f172a; padding: 2px 4px; border-radius: 4px; border: 1px solid #334155;">
                  <button type="button" class="icon-action-btn btn-xs" (click)="adjustFontSize('subtitle', -1)"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 20px; height: 20px; background: transparent; border: none; color: #e2e8f0; cursor: pointer;">-</button>
                  <span style="color: #94a3b8; font-size: 0.75rem; width: 20px; text-align: center;">{{
                    activeItem.properties.subtitleFontSize || 14 }}</span>
                  <button type="button" class="icon-action-btn btn-xs" (click)="adjustFontSize('subtitle', 1)"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 20px; height: 20px; background: transparent; border: none; color: #e2e8f0; cursor: pointer;">+</button>
                </div>

                <div
                  style="display: flex; align-items: center; background: #0f172a; border-radius: 4px; border: 1px solid #334155; overflow: hidden; height: 26px;">
                  <input type="color" name="subtitleColorPicker" [(ngModel)]="activeItem.properties.subtitleColor"
                    (ngModelChange)="syncDirtyState()"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 24px; height: 26px; padding: 0; border: none; cursor: pointer; background: transparent;"
                    title="Subtitle Color">
                  <input type="text" name="subtitleColorHex" [(ngModel)]="activeItem.properties.subtitleColor"
                    (ngModelChange)="syncDirtyState()"
                    [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                    style="width: 65px; height: 26px; border: none; background: transparent; color: #e2e8f0; font-size: 0.75rem; padding: 0 4px; text-transform: uppercase;"
                    placeholder="#FFF">
                </div>
              </div>
            </label>
            <input id="subTitle" type="text" name="subTitle" [(ngModel)]="activeItem.properties.subTitle"
              (ngModelChange)="syncDirtyState()" placeholder="e.g. Prints 2 copies of classic strips"
              [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)" style="width: 100%;">
          </div>

          <div class="form-group">
            <label for="priceClassicStrip">Price (Classic Strip) <span style="font-weight: normal; color: #94a3b8; font-size: 0.85em;">(Per 2 copies)</span></label>
            <input id="priceClassicStrip" type="number" name="priceClassicStrip"
              [(ngModel)]="activeItem.properties.priceClassicStrip" (ngModelChange)="syncDirtyState()" placeholder="e.g. 10.00"
              [disabled]="!!version">
          </div>

          <div class="form-group">
            <label for="priceMultiCut">Price (Multi-Cut) <span style="font-weight: normal; color: #94a3b8; font-size: 0.85em;">(Per 2 copies)</span></label>
            <input id="priceMultiCut" type="number" name="priceMultiCut"
              [(ngModel)]="activeItem.properties.priceMultiCut" (ngModelChange)="syncDirtyState()" placeholder="e.g. 12.00"
              [disabled]="!!version">
          </div>

          <div class="form-group">
            <label for="tileType">Tile Type</label>
            <select id="tileType" name="tileType" [(ngModel)]="activeItem.properties.tileType" (ngModelChange)="syncDirtyState()" class="form-control"
              [disabled]="!!version || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)">
              <option value="Signature">Signature</option>
              <option value="Theme">Theme</option>
              <option value="FeaturedFrame">FeaturedFrame</option>
            </select>
          </div>

          <div class="form-group" *ngIf="activeItem.properties.tileType === 'FeaturedFrame'">
            <label>Select Frame Group</label>

            <div class="custom-tree-select"
              style="max-height: 250px; overflow-y: auto; border: 1px solid #334155; border-radius: 4px; background: #0f172a; padding: 8px;">
              <div *ngIf="allFrameGroupsGrouped.length === 0 && !isLoadingFrameGroups"
                style="color: #64748b; font-size: 0.85rem; padding: 4px;">
                No machine-specific frame groups found.
              </div>
              <div *ngFor="let group of allFrameGroupsGrouped">
                <div (click)="group.expanded = !group.expanded"
                  style="cursor: pointer; padding: 6px; display: flex; align-items: center; background: #1e293b; margin-bottom: 4px; border-radius: 4px; user-select: none;">
                  <span style="margin-right: 8px; font-size: 0.75rem; color: #94a3b8;">{{ group.expanded ? '▼' : '▶'
                    }}</span>
                  <strong style="color: #cbd5e1; font-size: 0.9rem;">Theme: {{ group.themeId }}</strong>
                </div>
                <div *ngIf="group.expanded" style="padding-left: 20px; margin-bottom: 8px;">
                  <div *ngFor="let opt of group.frames" (click)="selectFrameGroup(opt)"
                    [style.background]="(activeItem.properties.featuredThemeId === opt.themeId && activeItem.properties.featuredFrameId === opt.frameId) ? 'rgba(59,130,246,0.2)' : 'transparent'"
                    style="cursor: pointer; padding: 6px; border-radius: 4px; display: flex; align-items: center; gap: 8px; margin-bottom: 2px; transition: background 0.2s;">
                    <input type="radio"
                      [checked]="activeItem.properties.featuredThemeId === opt.themeId && activeItem.properties.featuredFrameId === opt.frameId"
                      style="margin: 0; cursor: pointer; width: 14px; height: 14px; accent-color: #3b82f6;">
                    <span style="font-size: 0.85rem; color: #f8fafc; flex-grow: 1;">{{ opt.displayName }}</span>
                    <!-- NEW DELETE BUTTON -->
                    <button *ngIf="isFullEditorUnlocked || (!version && isGlobal)" (click)="deleteFrameGroup(opt, $event)"
                      title="Delete Frame Group"
                      style="background: none; border: none; color: #ef4444; cursor: pointer; padding: 0 4px;">🗑️</button>
                  </div>
                  <div *ngIf="group.frames.length === 0"
                    style="padding: 4px; color: #64748b; font-size: 0.85rem; font-style: italic;">No frame groups in
                    this theme</div>
                </div>
              </div>
            </div>


            <div *ngIf="isLoadingFrameGroups" style="font-size: 0.85rem; color: #94a3b8; margin-top: 4px;">
              <span class="spinner-inline" style="border-color: #3b82f6; border-bottom-color: transparent;"></span>
              Loading available frame groups...
            </div>
          </div>

          <div class="form-group"
            *ngIf="activeItem.properties.tileType === 'Theme' || activeItem.properties.tileType === 'Signature' || activeItem.properties.tileType === 'FeaturedFrame'">
            <button type="button" class="secondary-btn btn-block" (click)="openFramesEditor()"
              [disabled]="activeItem.properties.tileType === 'FeaturedFrame' && (!activeItem.properties.featuredThemeId || !activeItem.properties.featuredFrameId)">
              {{ version ? 'View Frames' : 'Set Frames' }}
            </button>
          </div>

          <div class="form-group">
            <label for="imageDirectory">Image / Asset Directory</label>
            <input id="imageDirectory" type="text" name="imageDirectory"
              [(ngModel)]="activeItem.properties.imageDirectory" placeholder="e.g. Assets/LocalArtists" disabled>
            <p class="input-helper">Automatically matches the Tile ID.</p>
          </div>

          <div class="form-group">
            <label>Theme Thumbnail</label>

            <div class="thumbnail-preview-container" *ngIf="isLoadingThumbnail"
              style="padding: 12px; background: rgba(15, 23, 42, 0.4); border-radius: 8px; text-align: center;">
              <span class="spinner-inline" style="border-color: #3b82f6; border-bottom-color: transparent;"></span>
              <span class="text-dim" style="font-size: 0.85rem;">Loading preview...</span>
            </div>

            <div class="thumbnail-preview-container" *ngIf="!isLoadingThumbnail && thumbnailPreview"
              style="margin-bottom: 12px;">
              <div
                style="background: rgba(15, 23, 42, 0.6); padding: 8px; border-radius: 8px; border: 1px solid #334155;">
                <img [src]="thumbnailPreview.url" alt="Theme Thumbnail"
                  style="max-width: 100%; height: auto; max-height: 180px; display: block; margin: 0 auto; border-radius: 4px; object-fit: contain;">
              </div>
              <p class="input-helper"
                style="margin-top: 6px; color: #34d399; font-weight: 500; display: flex; align-items: center; justify-content: space-between;">
                <span style="display: flex; align-items: center; gap: 4px;">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Current File: {{ thumbnailPreview.name }}
                </span>
                <button *ngIf="!version && (isFullEditorUnlocked || activeItem.properties.isCustomizable)"
                  class="icon-action-btn danger btn-xs" style="margin: 0; padding: 2px 6px; font-size: 0.85rem;"
                  (click)="removeThumbnail()" [disabled]="isUploadingThumbnail" title="Remove Thumbnail">🗑️</button>
              </p>
            </div>

            <div class="thumbnail-preview-container" *ngIf="!isLoadingThumbnail && !thumbnailPreview"
              style="padding: 16px; background: rgba(15, 23, 42, 0.4); border-radius: 8px; border: 1px dashed #475569; text-align: center; margin-bottom: 12px;">
              <p class="input-helper" style="color: #fbbf24; margin: 0;">No thumbnail uploaded yet.</p>
            </div>

            <ng-container *ngIf="!version && (isFullEditorUnlocked || activeItem.properties.isCustomizable)">
              <label for="themeThumbnail" class="secondary-btn btn-block" [class.disabled]="isUploadingThumbnail"
                style="text-align: center; cursor: pointer; display: inline-block; margin-bottom: 0;">
                <span *ngIf="!isUploadingThumbnail">📸 {{ thumbnailPreview ? 'Replace Thumbnail' : 'Upload Thumbnail'
                  }}</span>
                <span *ngIf="isUploadingThumbnail"><span class="spinner-inline"></span> Uploading...</span>
              </label>
              <input id="themeThumbnail" type="file" accept=".png" (change)="uploadThumbnail($event)"
                [disabled]="isUploadingThumbnail || (!isFullEditorUnlocked && !activeItem.properties.isCustomizable)"
                style="display: none;">
              <p class="input-helper" *ngIf="!isUploadingThumbnail">Select a PNG. The suffix '_TTB' will be
                automatically added.</p>
            </ng-container>
          </div>
          </fieldset>

          <!-- Position and Dimensions Info -->
          <div class="layout-info-grid">
            <div class="info-cell">
              <span class="label">X Position</span>
              <span class="value">{{ activeItem.x }}px</span>
            </div>
            <div class="info-cell">
              <span class="label">Y Position</span>
              <span class="value">{{ activeItem.y }}px</span>
            </div>
            <div class="info-cell">
              <span class="label">Tile Width</span>
              <span class="value">{{ activeItem.cols }}px</span>
            </div>
            <div class="info-cell">
              <span class="label">Tile Height</span>
              <span class="value">{{ activeItem.rows }}px</span>
            </div>
          </div>

          <!-- Pixel Dimensions -->
          <div class="pixel-dimensions-info" *ngIf="activeItem">
            <span class="label">Target Asset Size (FHD)</span>
            <span class="value">
              {{ activeItemPixelWidth }}<small>px</small> &times; {{ activeItemPixelHeight }}<small>px</small>
            </span>
          </div>

          <!-- Full Width Delete Action Button -->
          <div class="sidebar-delete-container" *ngIf="activeItem && !version">
            <button type="button" class="btn-delete-tile-full" (click)="removeTile(activeItem)">
              🗑️ Delete This Tile
            </button>
          </div>
        </form>
      </div>

      <div class="sidebar-footer-info">
        <p class="hint-text">💡 Grid items can be directly dragged to move them or resized from the bottom-right corner
          handles.</p>
      </div>
    </aside>
  </div>

  <!-- KAN-523 AC-3: Delete Standalone Tile Confirmation Modal -->
  <div class="editor-modal-overlay" *ngIf="deleteTileConfirmModal" role="dialog" aria-modal="true">
    <div class="editor-modal-panel">
      <div class="editor-modal-header">
        <h4>Delete Tile</h4>
        <button type="button" class="editor-modal-close" (click)="cancelDeleteStandaloneTile()" [disabled]="isDeletingStandaloneTile">&times;</button>
      </div>
      <div class="editor-modal-body">
        <p>{{ deleteTileConfirmModal.promptText }}</p>
      </div>
      <div class="editor-modal-footer">
        <button type="button" class="secondary-btn" (click)="cancelDeleteStandaloneTile()" [disabled]="isDeletingStandaloneTile">
          {{ housekeepingMessages.deleteTileCancelLabel }}
        </button>
        <button type="button" class="danger-modal-btn" (click)="confirmDeleteStandaloneTile()" [disabled]="isDeletingStandaloneTile">
          <span class="spinner-inline" *ngIf="isDeletingStandaloneTile"></span>
          <span>{{ housekeepingMessages.deleteTileConfirmLabel }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- KAN-523 AC-11: Publish to Device Unsaved-Changes Warning Modal -->
  <div class="editor-modal-overlay" *ngIf="isUnsavedPublishModalOpen" role="dialog" aria-modal="true">
    <div class="editor-modal-panel">
      <div class="editor-modal-header">
        <h4>Unsaved Layout Changes</h4>
        <button type="button" class="editor-modal-close" (click)="cancelUnsavedPublishModal()">&times;</button>
      </div>
      <div class="editor-modal-body">
        <p>{{ housekeepingMessages.publishUnsavedWarning }}</p>
      </div>
      <div class="editor-modal-footer">
        <button type="button" class="secondary-btn" (click)="cancelUnsavedPublishModal()">
          {{ housekeepingMessages.publishUnsavedCancelLabel }}
        </button>
        <button type="button" class="primary-btn" (click)="confirmUnsavedPublishModal()">
          {{ housekeepingMessages.publishUnsavedConfirmLabel }}
        </button>
      </div>
    </div>
  </div>
</div>

<app-theme-frames-editor *ngIf="isFramesEditorOpen"
  [themeId]="activeItem?.properties?.tileType === 'FeaturedFrame' ? (activeItem?.properties?.featuredThemeId || '') : (activeItem?.id || '')"
  [tileType]="activeItem?.properties?.tileType || ''" [featuredFrameId]="activeItem?.properties?.featuredFrameId"
  [isGlobal]="isFramesEditorGlobal" [deviceId]="deviceId" [version]="version" (closeEditor)="closeFramesEditor()">
</app-theme-frames-editor>

<app-global-frame-group-copy-modal *ngIf="isGlobalCopyModalOpen" [isCopying]="isCopyingGlobalGroup"
  (close)="closeGlobalCopyModal()" (copyGroup)="onCopyGlobalGroup($event)">
</app-global-frame-group-copy-modal>
